/**
 * Image optimization utilities for frontend
 * Uses optimized image versions from backend when available
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SIZE_WIDTHS = { thumb: 480, medium: 800, large: 1600 };

// Asks the server (or Unsplash) for a resized copy so phones download lighter images.
export const sizedImage = (url, w) => {
  if (!url || !w) return url || "";
  const base = url.split("?")[0];
  if (base.includes("/api/uploads/") && /\.(jpe?g|png|webp)$/i.test(base)) return `${base}?w=${w}`;
  if (url.includes("images.unsplash.com")) {
    return /[?&]w=\d+/.test(url) ? url.replace(/([?&])w=\d+/, `$1w=${w}`) : `${url}${url.includes("?") ? "&" : "?"}w=${w}`;
  }
  return url;
};

export const heroWidth = () => (typeof window !== "undefined" && window.innerWidth < 768 ? 800 : 1600);

/**
 * Get the appropriate image URL based on context/size needed
 * @param {Object|string} image - Image object with urls or simple URL string
 * @param {string} size - Desired size: 'thumb' (300px), 'medium' (800px), 'large' (1600px), 'original'
 * @returns {string} - The appropriate image URL
 */
export const getOptimizedImageUrl = (image, size = 'medium') => {
  // If it's just a string URL, return as-is
  if (typeof image === 'string') {
    return sizedImage(image, SIZE_WIDTHS[size]);
  }
  
  // If image object has urls property (optimized images from our backend)
  if (image?.urls) {
    const url = image.urls[size] || image.urls.medium || image.urls.original || image.url;
    // Prepend backend URL if it's a relative path
    if (url?.startsWith('/api/')) {
      return `${BACKEND_URL}${url}`;
    }
    return url;
  }
  
  // Fallback to url property
  if (image?.url) {
    if (image.url.startsWith('/api/')) {
      return `${BACKEND_URL}${image.url}`;
    }
    return image.url;
  }
  
  return '';
};

/**
 * Image size recommendations by usage context
 */
export const ImageSizes = {
  THUMBNAIL: 'thumb',      // 300x300 - For galleries, lists, admin previews
  CARD: 'medium',          // 800x800 - For cards, grid items
  HERO: 'large',           // 1600x1600 - For hero sections, full-width banners
  FULL: 'original',        // Original size - When needed
};

/**
 * Get srcSet for responsive images
 * @param {Object} image - Image object with urls
 * @returns {string} - srcSet string for img element
 */
export const getImageSrcSet = (image) => {
  if (typeof image === 'string' || !image?.urls) {
    return '';
  }
  
  const srcSetParts = [];
  const { urls } = image;
  
  if (urls.thumb) {
    const url = urls.thumb.startsWith('/api/') ? `${BACKEND_URL}${urls.thumb}` : urls.thumb;
    srcSetParts.push(`${url} 300w`);
  }
  if (urls.medium) {
    const url = urls.medium.startsWith('/api/') ? `${BACKEND_URL}${urls.medium}` : urls.medium;
    srcSetParts.push(`${url} 800w`);
  }
  if (urls.large) {
    const url = urls.large.startsWith('/api/') ? `${BACKEND_URL}${urls.large}` : urls.large;
    srcSetParts.push(`${url} 1600w`);
  }
  
  return srcSetParts.join(', ');
};

/**
 * Component-friendly image props generator
 * @param {Object|string} image - Image source
 * @param {string} size - Default size to use
 * @param {string} alt - Alt text
 * @returns {Object} - Props to spread on img element
 */
export const getImageProps = (image, size = 'medium', alt = '') => {
  const src = getOptimizedImageUrl(image, size);
  const srcSet = getImageSrcSet(image);
  
  return {
    src,
    ...(srcSet && { srcSet }),
    alt,
    loading: 'lazy',
  };
};

export default {
  getOptimizedImageUrl,
  getImageSrcSet,
  getImageProps,
  ImageSizes,
};
